import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  FileText,
  History,
  NotebookText,
  Paperclip,
  Pencil,
  Printer,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import StatusChip from "../components/StatusChip";
import AttachmentLink from "../components/AttachmentLink";
import StatusActionPanel from "../components/StatusActionPanel";
import SuggestionTimeline from "../components/SuggestionTimeline";
import RailReviewers from "../components/RailReviewers";
import EmailDeliverySection from "../components/EmailDeliverySection";
import SuggestionField from "../components/SuggestionField";
import SuggestionNotes from "../components/SuggestionNotes";
import CommitteeCard from "../components/CommitteeCard";
import EvaluationFormCard from "../components/EvaluationFormCard";
import ImplementationCard from "../components/ImplementationCard";
import VerificationCard from "../components/VerificationCard";
import {
  editSuggestion,
  getSuggestion,
  getSuggestionHistory,
  invalidateSuggestionCaches,
} from "../api/ess";
import { displayDepartment } from "../api/departments";
import { isAuthorized } from "../api/auth";
import { useUserProfile } from "../hooks/useUserProfile";
import { useDepartments, useDepartmentNames } from "../hooks/useDepartments";
import {
  COMMON_UNIT_LABEL,
  hasMultipleUnitGroups,
  sortDepartmentsByUnit,
  unitGroupLabel,
} from "../utils/departments";
import { Autocomplete, Chip, Select, TextField } from "../ds";
import {
  CATEGORIES,
  displayCategory,
  isOtherCategory,
  type EditSuggestionInput,
} from "../types/ess";
import { apiErrorMessage } from "../utils/apiError";
import { formatDateTime } from "../utils/format";
import { slaState, TERMINAL_CATEGORIES } from "../utils/sla";
import { statusCategory, useWorkflowConfig } from "../hooks/useWorkflow";
import {
  useRegisterPageTitle,
  useRegisterTopBarActions,
} from "../context/TopBarActionsContext";

function CardHeader({
  icon,
  title,
  extra,
}: {
  icon: React.ReactNode;
  title: string;
  extra?: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ mb: 2.5 }}
    >
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        })}
      >
        {icon}
      </Box>
      <Typography
        sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
      >
        {title}
      </Typography>
      {extra}
    </Stack>
  );
}

export default function SuggestionDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Inline edit (category + suggestion department) on the Submitter
  // Details card — replaces the old modal dialog. Drafts are seeded from
  // the suggestion when the user enters edit mode.
  const [editing, setEditing] = useState(false);
  const [draftCategory, setDraftCategory] = useState("");
  const [draftOtherCategory, setDraftOtherCategory] = useState("");
  const [draftDepartment, setDraftDepartment] = useState("");
  const { names: departmentOptions } = useDepartmentNames();
  // staleTime: detail/history are explicitly invalidated by mutations
  // in StatusActionPanel. Outside of that path, content changes rarely —
  // 60s prevents window-focus refetch thrash without affecting freshness.
  const detail = useQuery({
    queryKey: ["ess-detail", id],
    queryFn: () => getSuggestion(id),
    enabled: !!id,
    staleTime: 60_000,
  });

  const history = useQuery({
    queryKey: ["ess-history", id],
    queryFn: () => getSuggestionHistory(id),
    enabled: !!id,
    staleTime: 60_000,
  });

  // Probe `edit_suggestion` from the yaml — same gating mechanism as
  // accept/reject. Keyed on (id, status) so the probe re-runs after a
  // status mutation (the predicate restricts edits to the `submitted`
  // state). Errors fall back to "not authorized" so we never reveal
  // the button when the backend can't confirm permission.
  const canEdit = useQuery({
    queryKey: ["ess-auth-edit", id, detail.data?.status],
    queryFn: async () => {
      try {
        const res = await isAuthorized({
          action: "edit_suggestion",
          entity_type: "ess_suggestion",
          entity_id: id,
          workflow: "ess_suggestion",
        });
        return res.is_authorized;
      } catch {
        return false;
      }
    },
    enabled: !!id && !!detail.data,
    staleTime: 60_000,
  });

  const { data: workflow } = useWorkflowConfig();

  // Persist inline category/department edits. Only the changed fields are
  // sent so the audit trail captures the diff (mirrors the old dialog).
  const editMutation = useMutation({
    mutationFn: (input: EditSuggestionInput) => editSuggestion(id, input),
    onSuccess: () => {
      enqueueSnackbar("Suggestion updated", { variant: "success" });
      invalidateSuggestionCaches(qc, id);
      setEditing(false);
    },
    onError: (err: unknown) =>
      enqueueSnackbar(apiErrorMessage(err, "Edit failed"), {
        variant: "error",
      }),
  });

  const startEdit = () => {
    if (!detail.data) return;
    setDraftCategory(detail.data.category);
    setDraftOtherCategory(detail.data.other_category ?? "");
    setDraftDepartment(detail.data.department);
    setEditing(true);
  };

  const draftIsOther = isOtherCategory(draftCategory);
  const otherCategoryMissing = draftIsOther && !draftOtherCategory.trim();

  const handleSaveEdit = () => {
    if (!detail.data) return;
    if (otherCategoryMissing) {
      enqueueSnackbar("Please specify the category for 'Others'", {
        variant: "warning",
      });
      return;
    }
    const payload: EditSuggestionInput = {};
    if (draftCategory !== detail.data.category) payload.category = draftCategory;
    // Send the label whenever the effective category is "Others" and it
    // differs from what is stored; the backend clears it when the
    // category moves away from "Others", so nothing to send in that case.
    if (
      draftIsOther &&
      draftOtherCategory.trim() !== (detail.data.other_category ?? "")
    )
      payload.other_category = draftOtherCategory.trim();
    if (draftDepartment !== detail.data.department)
      payload.department = draftDepartment;
    if (Object.keys(payload).length === 0) {
      enqueueSnackbar("No changes to save", { variant: "info" });
      setEditing(false);
      return;
    }
    editMutation.mutate(payload);
  };

  // Submitter's own department(s) — distinct from the suggestion's
  // department. New rows stamp `metadata.submitter_department` at
  // submission time, so that's the source of truth going forward; legacy
  // rows (submitted before that field existed) fall back to the
  // submitter's live profile departments.
  const submitterProfile = useUserProfile(detail.data?.submitter_user_id);
  const metadataSubmitterDepartment =
    ((detail.data?.metadata as any)?.submitter_department as
      | string
      | null
      | undefined) || null;
  const profileDepartments = (
    submitterProfile.data?.user_info?.departments ?? []
  ).filter(Boolean) as string[];
  const submitterDepartments = metadataSubmitterDepartment
    ? [metadataSubmitterDepartment]
    : profileDepartments;
  // Only the legacy (profile-fallback) path depends on the profile
  // fetch — once metadata carries the department there's nothing to
  // wait on.
  const submitterDepartmentsLoading =
    !metadataSubmitterDepartment && submitterProfile.isLoading;

  // Unit is a property of the suggestion's department, not the
  // suggestion itself — resolve it from the departments list.
  const { data: departmentList } = useDepartments();
  const suggestionUnit = useMemo(
    () =>
      (departmentList ?? []).find((d) => d.name === detail.data?.department)
        ?.unit ?? null,
    [departmentList, detail.data?.department],
  );

  // Group the inline-edit department picker by unit when more than one unit is
  // present. MUI Autocomplete's groupBy needs options pre-sorted by group.
  const showDepartmentGroups = useMemo(
    () => hasMultipleUnitGroups(departmentList ?? []),
    [departmentList],
  );
  const groupedDepartmentOptions = useMemo(
    () => sortDepartmentsByUnit(departmentList ?? []).map((d) => d.name),
    [departmentList],
  );
  const departmentGroupByName = useMemo(() => {
    const m = new Map<string, string>();
    (departmentList ?? []).forEach((d) => {
      if (d?.name) m.set(d.name, unitGroupLabel(d));
    });
    return m;
  }, [departmentList]);

  // Dynamic top-bar title: "IPL-2026-048 · Submitted"
  const suggestionNumber = detail.data?.suggestion_number;
  const suggestionStatus = detail.data?.status;
  // Full status label in the top bar from md up (the rail shows it too, and
  // there is room); phones keep the short label, full one in the tooltip.
  const theme = useTheme();
  const fullStatusInTitle = useMediaQuery(theme.breakpoints.up("md"));
  const titleNode = useMemo(() => {
    if (!suggestionNumber) return null;
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        data-tour="detail-status"
      >
        <span>{suggestionNumber}</span>
        {suggestionStatus && (
          <StatusChip status={suggestionStatus} short={!fullStatusInTitle} />
        )}
      </Stack>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionNumber, suggestionStatus, fullStatusInTitle, workflow]);
  useRegisterPageTitle(titleNode);

  // Print button in the top bar. (Edit moved onto the Submitter Details
  // card as an inline-edit CTA — see that card below.)
  const topBarNode = useMemo(() => {
    const sId = detail.data?.id;
    if (!sId) return null;
    return (
      <Button
        variant="text"
        startIcon={<RavenIcon icon={Printer} />}
        aria-label="Print"
        onClick={() => {
          const url = `/suggestions/${sId}/print`;
          // No "noopener" feature string: with it window.open always returns
          // null, so the fallback below fired too and the current tab also
          // navigated to the print route. Cut the opener by hand instead.
          const w = window.open(url, "_blank");
          if (w) {
            w.opener = null;
          } else {
            window.location.href = url;
          }
        }}
        sx={(theme) => ({
          // Same active-nav copper as the Overview AppBar utility actions.
          color: theme.palette.navActive ?? theme.palette.primary.dark,
          minWidth: { xs: 40, sm: "auto" },
          px: { xs: 1, sm: 2 },
          "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
        })}
      >
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          Print
        </Box>
      </Button>
    );
  }, [detail.data?.id]);
  useRegisterTopBarActions(topBarNode);

  if (detail.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Alert severity="error">
        Could not load suggestion.{" "}
        {detail.error instanceof Error ? detail.error.message : ""}
      </Alert>
    );
  }

  const s = detail.data;

  // SLA badge (Task 14): 90-day countdown from submission. Only
  // meaningful while the suggestion is still open — once it lands in a
  // terminal category (closed/rejected/etc.) `daysLeft` keeps counting
  // against the live clock (not the closure time) and would show a
  // stale, ever-growing negative number, so the badge is hidden rather
  // than surfacing a number that no longer means anything operationally.
  // Whether a *closed* suggestion missed its SLA is instead surfaced via
  // Landing's "SLA breached" facet (uses the same `slaState`, but that
  // one deliberately looks at `breached`, which stays meaningful after
  // closure since it's judged against `updated_at`, not the clock).
  const sla = slaState(s, (status) => statusCategory(workflow, status));
  const isTerminalStatus = TERMINAL_CATEGORIES.has(
    statusCategory(workflow, s.status),
  );
  const slaChipNode =
    workflow && !isTerminalStatus ? (
      <Chip
        label={
          sla.overdue
            ? `SLA breached · ${Math.abs(sla.daysLeft)} days over`
            : `SLA: ${sla.daysLeft} days left`
        }
        colorVariant={sla.overdue ? "error" : "default"}
        chipVariant="outlined"
      />
    ) : undefined;

  return (
    <Box
      sx={{
        // Cancel AppLayout's content-area bottom padding (24px). That
        // padding sits BELOW the grid, so it adds 24px of over-scroll
        // beyond the sticky rail's range — which let the rail un-stick
        // and its header drift up by 24px at the bottom of the scroll.
        // Removing it makes maxScroll == the rail's sticky range.
        mb: { md: -3 },
      }}
    >
      {/* Two-column detail layout (mirrors the NMMS Incident screen):
          primary content cards on the left, the Status History audit
          trail in a narrower sticky right rail. Collapses to a single
          stacked column below md. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 7fr) minmax(0, 3fr)",
          },
          gap: { xs: 0, md: 3 },
          alignItems: "start",
        }}
      >
        {/* Left column — primary content. The footer-clearance bottom
            padding lives HERE (not on the outer Box) so the grid — the
            sticky right rail's containing block — is as tall as the
            scrollable content. If this padding sat below the grid, the
            rail would un-stick once you scrolled into it and its header
            would ride up out of view. */}
        <Box
          sx={{
            minWidth: 0,
            // Reserve scroll room below the last card so it clears the
            // fixed StatusActionPanel with a ~24px gap when scrolled to
            // bottom. Lives on the left column (not the outer Box) so the
            // grid stays the sticky rail's full-height container.
            pb: { xs: 15, sm: 8.5 },
          }}
        >
      {/* Card 1: Suggestion content (the actual proposal). Title +
        description + remarks live together because they're what the
        reader is here for; submitter metadata is secondary and lives
        in Card 2 below. */}
      <Card sx={{ mb: 2.5 }} data-tour="detail-suggestion-card">
        <CardContent>
          <CardHeader
            icon={<RavenIcon icon={FileText} sx={{ fontSize: 16 }} />}
            title="Suggestion"
            extra={slaChipNode}
          />
          <Box sx={{ mb: 1.5 }}>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}
            >
              Title
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {s.title}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}
            >
              Description
            </Typography>
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {s.description}
            </Typography>
          </Box>
          {s.remarks && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}
              >
                Latest remarks
              </Typography>
              <Typography variant="body1">{s.remarks}</Typography>
            </Box>
          )}
          {/* Attachments are clubbed into the Suggestion card (not a
              separate card) — they belong to the proposal itself.
              Separated from the body text by a hairline divider. */}
          {s.attachment_ids && s.attachment_ids.length > 0 && (
            <Box
              sx={{
                mt: 2.5,
                pt: 2.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Box
                  component="span"
                  sx={{ color: "primary.main", display: "inline-flex" }}
                >
                  <RavenIcon icon={Paperclip} sx={{ fontSize: 16 }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Attachments
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary" }}
                >
                  ({s.attachment_ids.length}{" "}
                  {s.attachment_ids.length === 1 ? "file" : "files"})
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                alignItems="flex-end"
              >
                {s.attachment_ids.map((aid) => (
                  <AttachmentLink key={aid} assetId={aid} />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Submitter metadata. Employee identity, department,
        category, dates, on-behalf-of. An inline-edit CTA (top-right)
        toggles category + suggestion department into editable controls. */}
      <Card sx={{ mb: 2.5 }} data-tour="detail-submitter">
        <CardContent>
          <CardHeader
            icon={<RavenIcon icon={FileText} sx={{ fontSize: 16 }} />}
            title="Submitter Details"
            extra={
              canEdit.data && !editing ? (
                <Button
                  variant="text"
                  startIcon={<RavenIcon icon={Pencil} />}
                  onClick={startEdit}
                  sx={(theme) => ({
                    color: theme.palette.navActive ?? theme.palette.primary.dark,
                    minWidth: "auto",
                    py: 0.25,
                    px: 1,
                    "& .MuiButton-startIcon": { mr: 0.5 },
                  })}
                >
                  Edit
                </Button>
              ) : undefined
            }
          />
          <Box
            sx={{
              display: "grid",
              // Two-column metadata grid matches NewSuggestion's
              // Submitter Information layout — denser than three columns
              // at the same reading width.
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: "16px 24px",
            }}
          >
            <SuggestionField label="Employee" value={s.employee_name} />
            <SuggestionField label="Employee ID" value={s.employee_id} />
            <SuggestionField
              label="Submitter Department"
              value={
                submitterDepartmentsLoading ? (
                  <Typography
                    component="span"
                    sx={{ color: "text.disabled", fontWeight: 400 }}
                  >
                    Loading…
                  </Typography>
                ) : submitterDepartments.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.25 }}
                  >
                    {submitterDepartments.map((d) => (
                      <Chip
                        key={d}
                        label={displayDepartment(d)}
                        colorVariant="primary"
                        chipVariant="outlined"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    component="span"
                    sx={{ color: "text.disabled", fontWeight: 400 }}
                  >
                    N/A
                  </Typography>
                )
              }
            />
            {editing ? (
              <Autocomplete
                label="Suggestion Department"
                options={
                  showDepartmentGroups ? groupedDepartmentOptions : departmentOptions
                }
                getOptionLabel={(d) => displayDepartment(d)}
                value={draftDepartment || null}
                onChange={(value) => setDraftDepartment(value ?? "")}
                groupBy={
                  showDepartmentGroups
                    ? (name) =>
                        departmentGroupByName.get(name) ?? COMMON_UNIT_LABEL
                    : undefined
                }
              />
            ) : (
              <SuggestionField
                label="Suggestion Department"
                value={displayDepartment(s.department)}
              />
            )}
            <SuggestionField
              label="Unit"
              value={
                suggestionUnit ?? (
                  <Typography
                    component="span"
                    sx={{ color: "text.disabled", fontWeight: 400 }}
                  >
                    N/A
                  </Typography>
                )
              }
            />
            {editing ? (
              <>
                <Select
                  label="Category"
                  value={draftCategory}
                  onChange={setDraftCategory}
                  options={CATEGORIES.map((c) => ({ label: c, value: c }))}
                />
                {draftIsOther && (
                  <TextField
                    label="Specify category *"
                    value={draftOtherCategory}
                    onChange={(e) => setDraftOtherCategory(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                    error={otherCategoryMissing}
                    helperText={
                      otherCategoryMissing ? "Required for 'Others'" : undefined
                    }
                  />
                )}
              </>
            ) : (
              <SuggestionField label="Category" value={displayCategory(s)} />
            )}
            <SuggestionField
              label="Submitted"
              value={formatDateTime(s.created_at)}
            />
            {/* Most suggestions are raised by the person themselves, so an
                always-present "On behalf of: N/A" row was a field that
                said nothing on almost every suggestion. */}
            {s.submitted_on_behalf_of && (
              <SuggestionField
                label="On behalf of"
                value={s.submitted_on_behalf_of}
              />
            )}
          </Box>
          {editing && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setEditing(false)}
                disabled={editMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveEdit}
                disabled={editMutation.isPending}
              >
                {editMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Committee seats (WC / SC), assignable while the suggestion is
          still with the custodian. Self-gating on the `assign_reviewers`
          auth probe; the roster stays readable in the right rail after
          the card disappears. */}
      <CommitteeCard suggestion={s} />

      {/* WC / SC evaluation form cards (Annexure-III / Annexure-IV).
          Both are self-gating: each renders its own editor only on its
          committee's review state for an authorized viewer, a read-only
          summary once an evaluation exists for that committee (any state,
          any viewer — it doubles as the audit record on later states),
          or nothing at all otherwise. */}
      <EvaluationFormCard committee="wc" suggestion={s} />
      <EvaluationFormCard committee="ssc" suggestion={s} />

      {/* Implementation, one row per department the SC nominated to
          verify the benefits. Self-gating on the stage plus the
          `record_department_implementation` probe; read-only once the
          backend has closed the stage. */}
      <ImplementationCard suggestion={s} />

      {/* Custodian review and closure — final step of the pipeline. Same
          self-gating shape as the evaluation cards above: editor on
          `implemented` for an authorized custodian, read-only audit
          block once `implementation_closed`, nothing otherwise. */}
      <VerificationCard suggestion={s} />

      {/* Notes — author · time · text · attachments. The composer inside
          self-hides unless /auth/is_authorized allows `add_note` for the
          caller at the current stage; the thread is always visible to
          anyone who can view the suggestion (incl. after approval). */}
      <Card sx={{ mb: 2.5 }} data-tour="detail-notes">
        <CardContent>
          <CardHeader
            icon={<RavenIcon icon={NotebookText} sx={{ fontSize: 16 }} />}
            title="Notes"
            extra={
              s.notes && s.notes.length > 0 ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  ({s.notes.length})
                </Typography>
              ) : undefined
            }
          />
          <SuggestionNotes suggestion={s} />
        </CardContent>
      </Card>

        </Box>

        {/* Right rail — Status History audit trail. Sticky on md+ so it
            stays in view while the (taller) left column scrolls, and
            given a tall min-height so it reads as a full-height side
            panel rather than a short floating card. alignSelf:start
            lets sticky engage (a stretched item can't stick). */}
        <Box
          sx={{
            position: { md: "sticky" },
            // Pin flush just below the 65px fixed AppBar. The negative
            // top margin cancels the content area's 24px top padding so
            // the panel header sits directly under the bar; the negative
            // right margin cancels the content area's right padding so
            // the panel runs to the viewport's right edge.
            mt: { md: -3 },
            mr: { md: -3 },
            top: { md: "65px" },
            alignSelf: { md: "start" },
          }}
        >
          <Card
            elevation={0}
            data-tour="detail-timeline"
            sx={{
              // Flat side rail (like the nav drawer): no shadow, white
              // fill. Only a hairline LEFT divider — explicitly drop the
              // default Card border on the other sides so the top edge
              // doesn't double up with the AppBar's bottom border.
              boxShadow: "none",
              borderRadius: 0,
              bgcolor: "background.paper",
              border: "none",
              borderLeft: { md: "1px solid" },
              borderColor: { md: "divider" },
              // Span from just under the AppBar (65px) all the way to
              // the viewport bottom, and lay the card out as a flex column
              // so its CardContent can be the internal scroll region.
              height: { md: "calc(100vh - 65px)" },
              display: { md: "flex" },
              flexDirection: { md: "column" },
            }}
          >
            {/* Internal scroll region on md+: Status History + the Assigned
                Reviewers summary scroll together inside the rail (the
                timeline can be taller than the viewport). `flex:1` +
                `minHeight:0` is what actually lets a flex child scroll —
                without minHeight:0 it refuses to shrink below its content
                and overflow never engages. A trailing spacer (not pb on
                CardContent, whose `:last-child` paddingBottom would win on
                specificity) clears the fixed StatusActionPanel footer so
                the last reviewer isn't hidden behind it. On xs the rail
                stacks and flows with the page; the spacer still clears the
                footer there. */}
            <CardContent
              sx={{
                flex: { md: 1 },
                minHeight: { md: 0 },
                overflowY: { md: "auto" },
              }}
            >
              <CardHeader
                icon={<RavenIcon icon={History} sx={{ fontSize: 16 }} />}
                title="Status History"
              />
              <SuggestionTimeline
                history={history.data}
                loading={history.isLoading}
                currentStatus={s.status}
                error={history.isError}
                onRetry={() => history.refetch()}
              />
              {/* Collapsible read-only reviewer roster — self-hides until
                  at least one reviewer is assigned. */}
              <RailReviewers
                key={s.id}
                reviewers={s.assigned_reviewers ?? []}
              />
              {/* Delivery log for the notification emails this suggestion
                  produced. Same collapsible rail idiom; resend is offered
                  to admins/custodians only. */}
              <EmailDeliverySection suggestionId={s.id} />
              {/* Footer-clearance spacer (mirrors the left column's pb). */}
              <Box aria-hidden sx={{ height: { xs: 120, sm: 68 } }} />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Action buttons (role-gated) */}
      <StatusActionPanel suggestion={s} />
    </Box>
  );
}
